import { use, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import defaultValues from 'shared/constants/defaultValues';
import { DeviceType } from 'shared/models/device-type';
import DeviceDetailsProviderContext from 'shared/providers/device-details/contexts/device-details-provider-context';
import DevicesProviderContext from 'shared/providers/devices/contexts/devices-provider-context';

import { DeviceMonitoringConstants } from 'features/device/device-details/views/device-details-settings/device-monitoring.component';
import CreateDeviceCommand from 'features/device/domain/models/create-device-command';
import Device from 'features/device/domain/models/device';
import DeviceCommand from 'features/device/domain/models/device-command';
import { DomipEventType } from 'features/device/domain/models/domip-event-type';
import GetLinkableSystemsResponse from 'features/device/domain/models/get-linkable-systems-response';
import IntercomMapping from 'features/device/domain/models/intercom-mapping';
import UpdateDeviceCommand from 'features/device/domain/models/update-device-command';
import { useCreateDevice, useGetLinkableSystems, useUpdateDevice } from 'features/device/domain/queries/device-queries';
import { DeviceCommandType } from 'features/device/models/device-command-type';
import { MediaserverVideoSetting } from 'features/mediaserver-settings/domain/models/mediaserver-video-setting';
import { useReadMediaserverVideoSettingQuery } from 'features/mediaserver-settings/domain/queries/media-video-queries';

export interface IDeviceDetailsHook {
  currentDevice: Device | undefined;
  handleClosePopup: (onCloseCallback?: () => void) => void;
  handleCancelEdit: () => void;
  handleSetEditMode: () => void;
  submitDevice: (fieldValues: FieldValues) => void;
  generateExternalId: () => void;
  generateProximityId: () => void;
  getDeviceCommand: (commandType: DeviceCommandType) => DeviceCommand;
  form: UseFormReturn<Partial<Device>, any>;
  allowUnlinking: boolean;
  isPopupOpen: boolean;
  isLoading: boolean;
  currentViewingMode: ViewingMode;
  currentDeviceType: DeviceType | undefined;
  currentGatewayId: string | undefined;
  currentSystemId: string | undefined;
  isMediaserverVideoAvailable: boolean;
  systemsResponse?: GetLinkableSystemsResponse;
  getDefaultExpectedRecurrence: () => number;
  showIpUnitUnreachableError: boolean;
  setShowIpUnitUnreachableError: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useDeviceDetailsHook = (): IDeviceDetailsHook => {
  const { showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const {
    mutate: createDeviceMutation,
    isSuccess: createDeviceIsSuccess,
    error: createDeviceError,
    data: createDeviceData,
  } = useCreateDevice();
  const {
    mutate: updateDeviceMutation,
    isSuccess: updateDeviceIsSuccess,
    error: updateDeviceError,
    data: updateDeviceData,
  } = useUpdateDevice();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMediaserverVideoAvailable, setIsMediaserverVideoAvailable] = useState<boolean>(false);

  const [currentGatewayId, setCurrentGatewayId] = useState<string | undefined>(undefined);
  const [currentSystemId, setCurrentSystemId] = useState<string | undefined>(undefined);
  const [showIpUnitUnreachableError, setShowIpUnitUnreachableError] = useState<boolean>(false);

  const {
    currentDevice,
    currentViewingMode,
    setCurrentViewingMode,
    reloadCurrentDeviceById: reloadCurrentDevice,
    form,
    isLoadingDevice,
    isSavingDevice,
    setIsSavingDevice,
    updateUnlinkWarningConfirmed,
  } = use(DeviceDetailsProviderContext);

  const { data: mediaserverVideoSettingResponse } = useReadMediaserverVideoSettingQuery();
  const { triggerReadDevicesQuery, triggerReadFilterValuesQuery, readDevicesQuery, closePopup, isPopupOpen, allowUnlinking } =
    use(DevicesProviderContext);

  const watchType = useWatch({ control: form.control, name: 'type' });
  const prevWatchTypeRef = useRef(watchType);
  const watchGatewayId = useWatch({ control: form.control, name: 'gatewayId' });
  const watchSystemId = useWatch({ control: form.control, name: 'systemId' });
  const currentDeviceType = watchType ?? currentDevice?.type;
  const hasSelectedType = watchType !== DeviceType.Unknown;
  const { data: systemsResponse, error: systemsResponseError } = useGetLinkableSystems(
    currentDeviceType,
    currentDeviceType === DeviceType.DesoDetectionLoop,
  );

  useEffect(() => {
    if (currentDevice?.type == DeviceType.DomipOutput) {
      const currentDeviceCommand = currentDevice?.commands?.find((entry) => entry.type === DeviceCommandType.DomipFunction);
      form.setValue('commands.0', { type: DeviceCommandType.DomipFunction, id: currentDeviceCommand?.id });
    }
  }, []);

  useEffect(() => {
    if (systemsResponseError) {
      setErrorMessage(systemsResponseError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemsResponseError]);

  useEffect(() => {
    if (currentViewingMode === 'creation' && prevWatchTypeRef.current !== watchType) {
      if (hasSelectedType) resetForm(watchType);
      prevWatchTypeRef.current = watchType;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewingMode, watchType]);

  useEffect(() => {
    setIsLoading(isLoadingDevice || isSavingDevice);
  }, [isLoadingDevice, isSavingDevice]);

  useEffect(() => {
    setIsMediaserverVideoAvailable(
      mediaserverVideoSettingResponse?.mediaserverVideoSetting === MediaserverVideoSetting.MediaserverVideoEnabled,
    );
  }, [mediaserverVideoSettingResponse]);

  useEffect(() => {
    setCurrentGatewayId(form.getValues().gatewayId ?? currentDevice?.gatewayId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchGatewayId]);

  useEffect(() => {
    setCurrentSystemId(form.getValues().systemId ?? currentDevice?.systemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchSystemId]);

  const handleResultFromApi = (isSuccess: boolean, error: Error | null, data: Device | undefined) => {
    if (isSuccess) {
      reloadCurrentDevice(data?.id);
      triggerReadDevicesQuery(readDevicesQuery);
      triggerReadFilterValuesQuery(undefined);
      setCurrentViewingMode('viewing');
    } else if (error instanceof AxiosError && error.status === 424) {
      setShowIpUnitUnreachableError(true);
    } else {
      setErrorMessage(error ?? undefined);
    }
  };

  const sanitizeGuid = (value: any) => (value === '' ? undefined : value);

  const createDevice = async (fieldValues: FieldValues, commands: DeviceCommand[]) => {
    const createCommand: CreateDeviceCommand = {
      externalId: fieldValues.externalId?.trim(),
      type: fieldValues.type,
      function: fieldValues.function,
      name: fieldValues.name?.trim(),
      note: fieldValues.note?.trim(),
      organisationUnitId: fieldValues.organisationUnitId,
      gatewayId: sanitizeGuid(fieldValues.gatewayId),
      systemId: sanitizeGuid(fieldValues.systemId),
      deviceProximityId: fieldValues.deviceProximityId,
      address: fieldValues.address?.trim(),
      source: fieldValues.source?.trim(),
      commands: commands,
      deviceMonitoring: fieldValues.deviceMonitoring,
      detectionLoopNumber: fieldValues.detectionLoopNumber,
      intercomMappings: (fieldValues.intercomMappings ?? []).map((mapping: IntercomMapping) => ({
        name: mapping.name.trim(),
        buttonId: mapping.buttonId.trim(),
        systemId: mapping.systemId ?? '',
      })),
    };
    setIsSavingDevice(true);
    createDeviceMutation(createCommand);
  };

  useEffect(() => {
    if (createDeviceIsSuccess || createDeviceError) {
      handleResultFromApi(createDeviceIsSuccess, createDeviceError, createDeviceData);
      setIsSavingDevice(false);
    }
  }, [createDeviceIsSuccess, createDeviceError, createDeviceData]);

  const updateDevice = async (fieldValues: FieldValues, commands: DeviceCommand[]) => {
    const updateCommand: UpdateDeviceCommand = {
      id: currentDevice?.id ?? '',
      externalId: fieldValues.externalId?.trim(),
      function: fieldValues.function,
      name: fieldValues.name?.trim(),
      note: fieldValues.note?.trim(),
      organisationUnitId: fieldValues.organisationUnitId,
      gatewayId: sanitizeGuid(fieldValues.gatewayId),
      systemId: sanitizeGuid(fieldValues.systemId),
      deviceProximityId: fieldValues.deviceProximityId,
      address: fieldValues.address?.trim(),
      source: fieldValues.source?.trim(),
      commands: commands,
      deviceMonitoring: fieldValues.deviceMonitoring,
      detectionLoopNumber: fieldValues.detectionLoopNumber,
      functionalName: fieldValues.functionalName?.trim(),
      eventTypeLow: fieldValues.eventTypeLow === DomipEventType.NoEvent ? undefined : fieldValues.eventTypeLow,
      eventTypeHigh: fieldValues.eventTypeHigh === DomipEventType.NoEvent ? undefined : fieldValues.eventTypeHigh,
      intercomMappings: (fieldValues.intercomMappings ?? []).map((mapping: IntercomMapping) => ({
        name: mapping.name.trim(),
        buttonId: mapping.buttonId.trim(),
        systemId: mapping.systemId ?? '',
      })),
    };

    setIsSavingDevice(true);
    updateDeviceMutation(updateCommand);
  };

  useEffect(() => {
    if (updateDeviceIsSuccess || updateDeviceError) {
      handleResultFromApi(updateDeviceIsSuccess, updateDeviceError, updateDeviceData);
      setIsSavingDevice(false);
      updateUnlinkWarningConfirmed(false);
    }
  }, [updateDeviceIsSuccess, updateDeviceError, updateDeviceData]);

  const submitDevice = (fieldValues: FieldValues) => {
    let index = 0;
    const filteredCommands: DeviceCommand[] = [];
    fieldValues.commands?.forEach((element: DeviceCommand) => {
      if (element.action === defaultValues.defaultSelectValue) {
        return;
      }

      element.id = element.id === '' ? undefined : element.id;
      element.action = element.action?.trim();
      element.username = element.action?.trim() === '' ? undefined : element.username;
      element.password = element.action?.trim() === '' ? undefined : element.password;
      filteredCommands[index] = element;
      index++;
    });

    if (currentViewingMode === 'creation') {
      createDevice(fieldValues, filteredCommands);
    } else {
      updateDevice(fieldValues, filteredCommands);
    }
  };

  const handleCancelEdit = () => {
    resetForm();
    setCurrentViewingMode('viewing');
  };

  const resetForm = (type?: DeviceType) => {
    form.reset(type ? { type } : undefined);
  };

  const handleSetEditMode = () => {
    setCurrentViewingMode('editing');
  };

  const handleClosePopup = (onCloseCallback?: () => void) => {
    showDefaultConfirmationPopUp({
      shouldShow: (currentViewingMode === 'creation' || currentViewingMode === 'editing') && form.formState.isDirty,
      action: () => {
        setCurrentViewingMode('viewing');
        closePopup();
        if (onCloseCallback) {
          onCloseCallback();
        }
      },
    });
  };

  const generateExternalId = () => {
    form.setValue('externalId', uuidv4(), { shouldDirty: true });
  };

  const generateProximityId = () => {
    form.setValue('deviceProximityId', uuidv4(), { shouldDirty: true });
  };

  const getDeviceCommand = (commandType: DeviceCommandType) => {
    const currentDeviceCommand = currentDevice?.commands?.find((entry) => entry.type === commandType);
    return (
      currentDeviceCommand ?? {
        type: commandType,
      }
    );
  };

  const getDefaultExpectedRecurrence = (): number => {
    if (currentDevice?.deviceMonitoring?.expectedRecurrence) {
      return currentDevice.deviceMonitoring.expectedRecurrence;
    }

    const deviceType = form.getValues('type');
    switch (deviceType) {
      case DeviceType.Universal:
      case DeviceType.UniversalAudio:
      case DeviceType.UniversalCamera:
      case DeviceType.UniversalIntercom:
        return DeviceMonitoringConstants.OneHourValue;
      case DeviceType.UniversalAlarmTransmitter:
        return DeviceMonitoringConstants.TwentyFourHoursValue;
      case DeviceType.G2Transmitter:
      case DeviceType.G2SmokeAlarm:
      case DeviceType.G2MotionDetector:
      case DeviceType.G2DoorContact:
      case DeviceType.G2TableAlarm:
      case DeviceType.G2Contact:
      case DeviceType.G2ToiletAlarm:
        return DeviceMonitoringConstants.SeventyTwoHoursValue;
      default:
        return DeviceMonitoringConstants.OneHourValue;
    }
  };

  return {
    currentDevice,
    submitDevice,
    handleClosePopup,
    handleCancelEdit,
    handleSetEditMode,
    generateExternalId,
    generateProximityId,
    currentDeviceType,
    currentGatewayId,
    currentSystemId,
    getDeviceCommand,
    form,
    isLoading,
    allowUnlinking,
    isPopupOpen,
    currentViewingMode,
    isMediaserverVideoAvailable,
    systemsResponse,
    getDefaultExpectedRecurrence,
    showIpUnitUnreachableError,
    setShowIpUnitUnreachableError,
  };
};
